import { ChurnBand } from '@shared/clientTypes/DTOs/ChurnDTO';
import { CHURN_HIGH_THRESHOLD, CHURN_MEDIUM_THRESHOLD } from '@shared/constants';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import { ReactNode } from 'react';

const BAND_STYLES: Record<ChurnBand, { label: string; className: string; dot: string }> = {
  high: {
    label: 'High risk',
    className: 'bg-customRed/15 text-customRed border-customRed/40',
    dot: 'bg-customRed',
  },
  medium: {
    label: 'Medium risk',
    className: 'bg-customGold/15 text-customGold border-customGold/40',
    dot: 'bg-customGold',
  },
  low: {
    label: 'Low risk',
    className: 'bg-customGreen/15 text-customGreen border-customGreen/40',
    dot: 'bg-customGreen',
  },
};

export const churnBandOf = (score: number | null | undefined): ChurnBand | null => {
  if (typeof score !== 'number') return null;
  if (score >= CHURN_HIGH_THRESHOLD) return 'high';
  if (score >= CHURN_MEDIUM_THRESHOLD) return 'medium';
  return 'low';
};

export const churnBandTone = (band?: ChurnBand | null) =>
  band === 'high'
    ? 'text-customRed'
    : band === 'medium'
    ? 'text-customGold'
    : band === 'low'
    ? 'text-customGreen'
    : 'text-customGrey';

interface ChurnRiskBadgeProps {
  score: number | null | undefined;
  band?: ChurnBand;
  size?: 'small' | 'large';
  suffix?: ReactNode;
  emptyText?: string;
  className?: string;
}

const ChurnRiskBadge = (props: ChurnRiskBadgeProps) => {
  const { score, size = 'small', suffix, emptyText = '—', className } = props;
  const band = props.band ?? churnBandOf(score);

  if (!band || typeof score !== 'number') {
    return <span className={mergeClassNames('text-customGrey', className)}>{emptyText}</span>;
  }

  const style = BAND_STYLES[band];

  return (
    <span
      title={`Churn risk ${Math.round(
        score
      )}/100 · medium ≥ ${CHURN_MEDIUM_THRESHOLD}, high ≥ ${CHURN_HIGH_THRESHOLD}`}
      className={mergeClassNames(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border font-semibold',
        size === 'large' ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs',
        style.className,
        className
      )}
    >
      <span className={mergeClassNames('h-2 w-2 rounded-full', style.dot)} />
      <span>{style.label}</span>
      <span className="tabular-nums opacity-80">{Math.round(score)}</span>
      {suffix}
    </span>
  );
};

export default ChurnRiskBadge;
